import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactLoading from 'react-loading';
import useUser from "../../services/auth/useUser";
import Main from "../../modules/main/main";
import DashboardApi from "../../services/api/dashboard";
import SfaDashboardApi from "../../services/api/sfa-dashboard";
import StatisticsCards from "../sfa-dashboard/components/StatisticsCards";
import StatusBreakdown from "../sfa-dashboard/components/StatusBreakdown";
import MonthlyTrendsChart from "../sfa-dashboard/components/MonthlyTrendsChart";
import TopSchoolsChart from "../sfa-dashboard/components/TopSchoolsChart";
import { toast } from 'react-toastify';

function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [dashboardValues, setDashboardValues] = useState();
    const [sfaDashboardData, setSfaDashboardData] = useState(null);
    const [sfaLoading, setSfaLoading] = useState(true);
    const user = useUser();
    const [parentView, setParentView] = useState(false);
    const navigate = useNavigate();

    const fetchDashboardCharts = async () => {
        try {
            const data = await DashboardApi.getAll();
            setDashboardValues(data.bar_chart_data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSfaDashboardData = async () => {
        try {
            setSfaLoading(true);
            const response = await SfaDashboardApi.getSummary();
            
            if (response.success) {
                setSfaDashboardData(response.data);
            } else {
                toast.error('Failed to load SFA dashboard data');
            }
        } catch (error) {
            console.error('Error fetching SFA dashboard data:', error);
            toast.error('Error loading SFA dashboard data');
        } finally {
            setSfaLoading(false);
        }
    };

    let breadcrumbs = [
        {"link": "/", "name": "Dashboard", "active": false}
    ];

    useEffect(() => {
        if (!parentView) {
            setLoading(true);
            fetchDashboardCharts();
            fetchSfaDashboardData();
        } else {
            setLoading(false);
            setSfaLoading(false);
        }
    }, [parentView]);

    useEffect(() => {
        if (user.user_permissions[0].slug === 'parent' || user.user_permissions[0].slug === 'teacher') {
            navigate('/applications');
        }
    }, []);

    return (
        <Main title="Dashboard" breadcrumbs={breadcrumbs} loading={loading}>
            {/* Parent View Toggle (commented out but preserved) */}
            {/*<div className="row">*/}
            {/*    <div className="ml-auto">*/}
            {/*        <label>*/}
            {/*            <span>Admin View</span>*/}
            {/*            <Toggle*/}
            {/*                defaultChecked={parentView}*/}
            {/*                icons={false}*/}
            {/*                onChange={() => setParentView(!parentView)} />*/}
            {/*            <span>Parent View</span>*/}
            {/*        </label>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Parent View */}
            <div className={parentView ? "row" : "row d-none"}>
                <div className="col-md-3 col-sm-6 col-12">
                    <div className="info-box bg-warning">
                        <div className="info-box-content">
                            <span className="info-box-text">Application Status</span>
                            <div className="progress">
                                <div className="progress-bar" style={{width: '70%'}}/>
                            </div>
                            <span className="progress-description">You are 70% Done.</span>
                            <div className="ml-auto text-bold">
                                <Link className="text-white text-uppercase" to="/sfa">Complete
                                    Application <i className="fas fa-arrow-right"/></Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Admin View - Enhanced with SFA Dashboard */}
            <div className={parentView ? "row d-none" : "row"}>
                {/* SFA Dashboard Statistics */}
                {sfaDashboardData && (
                    <>
                        <div className="col-12 mb-4">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <i className="fas fa-chart-pie mr-2"></i>
                                        SFA Dashboard Overview
                                    </h3>
                                    <div className="card-tools">
                                        <Link to="/sfa-dashboard" className="btn btn-primary btn-sm">
                                            <i className="fas fa-external-link-alt mr-1"></i>
                                            Full SFA Dashboard
                                        </Link>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <StatisticsCards statistics={sfaDashboardData.statistics} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Breakdown and Monthly Trends */}
                        <div className="col-lg-6">
                            <StatusBreakdown 
                                statusBreakdown={sfaDashboardData.status_breakdown}
                                onStatusClick={(status) => {
                                    // Navigate to full SFA dashboard with filter
                                    window.location.href = `/sfa-dashboard?status=${status}`;
                                }}
                            />
                        </div>
                        <div className="col-lg-6">
                            <MonthlyTrendsChart trends={sfaDashboardData.monthly_trends || []} />
                        </div>

                        {/* Recent SFA Applications */}
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <i className="fas fa-clock mr-2"></i>
                                        Recent SFA Applications
                                    </h3>
                                    <div className="card-tools">
                                        <Link to="/sfa-dashboard" className="btn btn-info btn-sm">
                                            View All
                                        </Link>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {sfaDashboardData.recent_sfas && sfaDashboardData.recent_sfas.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Student</th>
                                                        <th>School</th>
                                                        <th>Status</th>
                                                        <th>Created</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sfaDashboardData.recent_sfas.slice(0, 5).map((sfa) => (
                                                        <tr key={sfa.id}>
                                                            <td><strong>#{sfa.id}</strong></td>
                                                            <td>
                                                                <div>
                                                                    <strong>
                                                                        {sfa.sfa_student_info?.first_name} {sfa.sfa_student_info?.last_name}
                                                                    </strong>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {sfa.user?.email}
                                                                    </small>
                                                                </div>
                                                            </td>
                                                            <td>{sfa.sfa_school_info?.school_name || '-'}</td>
                                                            <td>
                                                                {sfa.sfa_review?.status ? (
                                                                    <span className={`badge badge-${
                                                                        sfa.sfa_review.status === 'approved' ? 'success' :
                                                                        sfa.sfa_review.status === 'rejected' ? 'danger' :
                                                                        sfa.sfa_review.status === 'in_review' ? 'secondary' :
                                                                        'info'
                                                                    }`}>
                                                                        {sfa.sfa_review.status.replace('_', ' ').toUpperCase()}
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge badge-warning">Pending Review</span>
                                                                )}
                                                            </td>
                                                            <td>{new Date(sfa.created_at).toLocaleDateString()}</td>
                                                            <td>
                                                                <div className="btn-group" role="group">
                                                                    <Link 
                                                                        to={`/sfa/${sfa.id}`} 
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        title="View/Edit SFA"
                                                                    >
                                                                        <i className="fas fa-eye"></i>
                                                                    </Link>
                                                                    {sfa.complete && !sfa.sfa_review && (
                                                                        <Link 
                                                                            to={`/sfa-review/${sfa.id}`} 
                                                                            className="btn btn-sm btn-outline-success"
                                                                            title="Review SFA"
                                                                        >
                                                                            <i className="fas fa-search"></i>
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="fas fa-inbox fa-2x text-muted mb-2"></i>
                                            <p className="text-muted">No recent SFA applications found.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Pending Review SFAs */}
                        {sfaDashboardData.pending_review_sfas && sfaDashboardData.pending_review_sfas.length > 0 && (
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                                            Pending Review ({sfaDashboardData.pending_review_sfas.length})
                                        </h3>
                                        <div className="card-tools">
                                            <Link to="/sfa-dashboard?status=pending_review" className="btn btn-warning btn-sm">
                                                Review All
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Student</th>
                                                        <th>School</th>
                                                        <th>Created</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sfaDashboardData.pending_review_sfas.slice(0, 3).map((sfa) => (
                                                        <tr key={sfa.id}>
                                                            <td><strong>#{sfa.id}</strong></td>
                                                            <td>
                                                                <strong>
                                                                    {sfa.sfa_student_info?.first_name} {sfa.sfa_student_info?.last_name}
                                                                </strong>
                                                            </td>
                                                            <td>{sfa.sfa_school_info?.school_name || '-'}</td>
                                                            <td>{new Date(sfa.created_at).toLocaleDateString()}</td>
                                                            <td>
                                                                <Link 
                                                                    to={`/sfa-review/${sfa.id}`} 
                                                                    className="btn btn-sm btn-warning"
                                                                >
                                                                    <i className="fas fa-search mr-1"></i>
                                                                    Review
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Original Dashboard Content */}
                <div className="col-lg-6">
                    <div className="card">
                        <div className={loading ? "overlay" : "overlay d-none"}>
                            <ReactLoading type="spin" color="black" width={'10%'} height={'10%'} />
                        </div>
                        <div className="card-header border-0">
                            <div className="d-flex justify-content-between">
                                <h3 className="card-title">Applications Approved and Paid Out</h3>
                                <Link to="/reports">View Report</Link>
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Original dashboard content can go here */}
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title m-0">Quick Actions</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6 mb-3">
                                    <Link to="/sfa" className="btn btn-primary btn-block">
                                        <i className="fas fa-plus mr-2"></i>
                                        New SFA
                                    </Link>
                                </div>
                                <div className="col-6 mb-3">
                                    <Link to="/empower" className="btn btn-warning btn-block">
                                        <i className="fas fa-plus mr-2"></i>
                                        New Empower
                                    </Link>
                                </div>
                                <div className="col-6 mb-3">
                                    <Link to="/sfa-dashboard" className="btn btn-info btn-block">
                                        <i className="fas fa-chart-pie mr-2"></i>
                                        SFA Dashboard
                                    </Link>
                                </div>
                                <div className="col-6 mb-3">
                                    <Link to="/reports" className="btn btn-success btn-block">
                                        <i className="fas fa-download mr-2"></i>
                                        Reports
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Super Admin Section */}
                {(user.user_permissions && user.user_permissions.length > 0 && user.user_permissions[0].slug === 'super-admin') && (
                    <div className="col-12 mt-4">
                        <div className="card border-danger">
                            <div className="card-header bg-danger">
                                <h5 className="card-title m-0 text-white">
                                    <i className="fas fa-shield-alt mr-2"></i>
                                    Super Admin Access
                                </h5>
                            </div>
                            <div className="card-body">
                                <p className="mb-3">You have Super Admin privileges. Access the Super Admin control panel for advanced system management.</p>
                                <Link to="/admin" className="btn btn-danger">
                                    <i className="fas fa-shield-alt mr-2"></i>
                                    Super Admin Dashboard
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Main>
    );
}

export default Dashboard;